/**
 * Compliance Decoration Plugin — renders compliance status highlighting
 * as ProseMirror decorations instead of AST marks.
 *
 * Uses CitationNodes as boundary markers: text between paragraph-start
 * (or previous citation) and a CitationNode belongs to that citation's claim.
 * The plugin looks up each claimId in an external statusMap to determine
 * the decoration style.
 *
 * Update the statusMap by dispatching:
 *   tr.setMeta(complianceDecoPluginKey, newMap)
 *
 * Set the active (surgery-mode) highlight by dispatching:
 *   tr.setMeta('complianceActiveId', claimId)   // or null to clear
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Extension } from '@tiptap/react'

export type ComplianceStatus = 'verified' | 'partial' | 'unverified' | 'contradicted' | 'unsupported'

// ── Plugin key (use to dispatch status updates and read state) ────────────────

export const complianceDecoPluginKey = new PluginKey<ComplianceDecoState>('complianceDecorations')

/** Meta key for setting the active (surgery-mode) compliance highlight */
export const COMPLIANCE_ACTIVE_META = 'complianceActiveId'

interface ComplianceDecoState {
  statusMap: Map<string, ComplianceStatus>
  activeComplianceId: string | null
  decorations: DecorationSet
}

const STATUS_CLASSES: Record<ComplianceStatus, string> = {
  verified: 'compliance-deco compliance-verified',
  partial: 'compliance-deco compliance-partial',
  unverified: 'compliance-deco compliance-unverified',
  contradicted: 'compliance-deco compliance-contradicted',
  unsupported: 'compliance-deco compliance-unsupported',
}

// ── Decoration builder ───────────────────────────────────────────────────────

/** Resolve a citation node's identity → status. Citation nodes from the
 *  PR4d backend carry BOTH `claimId` (the per-sentence DB row id) and
 *  `attachmentId` (the per-attachment uuid). Consumers may key statusMap by
 *  either id depending on which rebind step they're at — we try the more
 *  specific (attachmentId) first, then fall back to claimId. */
function resolveCitationStatus(
  attrs: Record<string, unknown>,
  statusMap: Map<string, ComplianceStatus>,
): { id: string; status: ComplianceStatus | undefined } | null {
  const attachmentId =
    typeof attrs.attachmentId === 'string' ? attrs.attachmentId : null
  const claimId = typeof attrs.claimId === 'string' ? attrs.claimId : null

  // Prefer attachmentId only if statusMap actually has an entry for it.
  // Otherwise fall back to claimId (current default; statusMap is built by
  // Workbench.buildClaimStatusMap and keyed by claimId until PR4d rebinds).
  if (attachmentId && statusMap.has(attachmentId)) {
    return { id: attachmentId, status: statusMap.get(attachmentId) }
  }
  if (claimId) {
    return { id: claimId, status: statusMap.get(claimId) }
  }
  if (attachmentId) {
    return { id: attachmentId, status: undefined }
  }
  return null
}

function buildComplianceDecos(
  doc: EditorState['doc'],
  statusMap: Map<string, ComplianceStatus>,
  activeId: string | null,
): DecorationSet {
  if (statusMap.size === 0) return DecorationSet.empty

  const decos: Decoration[] = []

  doc.descendants((node, pos) => {
    // Decorate every textblock that carries inline content (paragraph, heading,
    // and the paragraphs nested inside blockquote / list_item). Wrapper nodes
    // (blockquote, bulletList, orderedList, listItem) are not textblocks, so
    // the iterator descends into them to reach their inner textblock children.
    // codeBlock is a textblock but never contains citation nodes today.
    if (!node.isTextblock) return

    // Collect citation nodes within this textblock (resolving id by attachmentId
    // OR claimId — see resolveCitationStatus for the precedence rule).
    const citations: { pos: number; nodeSize: number; id: string }[] = []
    let childPos = pos + 1 // +1 for textblock open tag
    node.forEach((child) => {
      if (child.type.name === 'citation') {
        const resolved = resolveCitationStatus(child.attrs, statusMap)
        if (resolved) {
          citations.push({ pos: childPos, nodeSize: child.nodeSize, id: resolved.id })
        }
      }
      childPos += child.nodeSize
    })

    if (citations.length === 0) return false // no citations → no decorations on this textblock

    // Decorate text spans: [paraStart..cit1], [cit1+size..cit2], [citN+size..paraEnd]
    let spanStart = pos + 1
    for (const cit of citations) {
      const status = statusMap.get(cit.id)
      if (status && cit.pos > spanStart) {
        const isActive = cit.id === activeId
        decos.push(
          Decoration.inline(spanStart, cit.pos, {
            class: STATUS_CLASSES[status] + (isActive ? ' compliance-active' : ''),
            'data-compliance-id': cit.id,
          }),
        )
      }
      spanStart = cit.pos + cit.nodeSize // skip past citation node
    }

    // Trailing text after last citation
    const lastCit = citations[citations.length - 1]
    const paraEnd = pos + node.nodeSize - 1 // -1 for textblock close tag
    const lastStatus = statusMap.get(lastCit.id)
    if (lastStatus && spanStart < paraEnd) {
      const isActive = lastCit.id === activeId
      decos.push(
        Decoration.inline(spanStart, paraEnd, {
          class: STATUS_CLASSES[lastStatus] + (isActive ? ' compliance-active' : ''),
          'data-compliance-id': lastCit.id,
        }),
      )
    }

    return false // already iterated this textblock's inline children via forEach
  })

  return DecorationSet.create(doc, decos)
}

// ── ProseMirror Plugin ───────────────────────────────────────────────────────

const complianceDecoPMPlugin = new Plugin<ComplianceDecoState>({
  key: complianceDecoPluginKey,

  state: {
    init() {
      return { statusMap: new Map(), activeComplianceId: null, decorations: DecorationSet.empty }
    },
    apply(tr: Transaction, prev: ComplianceDecoState, _oldState, newState) {
      let { statusMap, activeComplianceId } = prev
      let needsRebuild = false

      // Check for status map update via meta
      const newMap: Map<string, ComplianceStatus> | undefined = tr.getMeta(complianceDecoPluginKey)
      if (newMap !== undefined) {
        statusMap = newMap
        needsRebuild = true
      }

      // Check for active highlight update via meta
      const newActiveId: string | null | undefined = tr.getMeta(COMPLIANCE_ACTIVE_META)
      if (newActiveId !== undefined) {
        activeComplianceId = newActiveId
        needsRebuild = true
      }

      // Doc changed — recompute with existing state
      if (tr.docChanged) {
        needsRebuild = true
      }

      if (needsRebuild) {
        return {
          statusMap,
          activeComplianceId,
          decorations: buildComplianceDecos(newState.doc, statusMap, activeComplianceId),
        }
      }

      return prev
    },
  },

  props: {
    decorations(state) {
      return complianceDecoPluginKey.getState(state)?.decorations ?? DecorationSet.empty
    },
  },
})

// ── TipTap Extension ─────────────────────────────────────────────────────────

export const ComplianceDecorationExtension = Extension.create({
  name: 'complianceDecoration',
  addProseMirrorPlugins() {
    return [complianceDecoPMPlugin]
  },
})
